import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { useLang } from '../utils/LanguageContext';
import { useAuth } from '../utils/AuthContext';
import { fileToPayload } from '../utils/menuImport';
import { t } from '../utils/translations';
import './MenuEditor.css';
import Loader from '../components/Loader';

const DEFAULT_CATEGORIES = ['Hot Drinks', 'Cold Drinks', 'Food'];

// Flip to true once an AI key (Claude) is configured on the menu-import
// Edge Function. While false, the import button shows a "coming soon" note.
const MENU_IMPORT_ENABLED = false;

function MenuEditor() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [importing, setImporting] = useState(false);
  const [importItems, setImportItems] = useState(null);
  const [importError, setImportError] = useState('');
  const [importSoon, setImportSoon] = useState(false);
  const fileInputRef = useRef(null);

  const { lang } = useLang();
  const { org, isPro, refreshProfile } = useAuth();
  const tr = t[lang];

  const orgCategories = org?.categories?.length ? org.categories : DEFAULT_CATEGORIES;
  // Also show categories that still exist on products but were removed
  // from the shop's list, so no product ever becomes invisible.
  const extraCategories = [...new Set(products.map(p => p.category))]
    .filter(c => c && !orgCategories.includes(c));
  const CATEGORIES = [...orgCategories, ...extraCategories];

  const [form, setForm] = useState({ name: '', price: '', cost: '', category: orgCategories[0], stock: '' });

  function getCatLabel(cat) {
    if (cat === 'Hot Drinks') return tr.catHotDrinks;
    if (cat === 'Cold Drinks') return tr.catColdDrinks;
    if (cat === 'Food') return tr.catFood;
    return cat;
  }

  useEffect(() => { fetchProducts(); }, []);

  async function fetchProducts() {
    const { data } = await supabase
      .from('products')
      .select('*')
      .order('category');
    setProducts(data || []);
    setLoading(false);
  }

  function openAdd() {
    setEditing(null);
    setForm({ name: '', price: '', cost: '', category: orgCategories[0], stock: '' });
    setShowForm(true);
  }

  function openEdit(p) {
    setEditing(p.id);
    setForm({ name: p.name, price: p.price, cost: p.cost || '', category: p.category, stock: p.stock || 0 });
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditing(null);
  }

  async function saveProduct() {
    if (!form.name || !form.price) return;
    setSaving(true);
    const fields = {
      name: form.name,
      price: parseFloat(form.price),
      cost: parseFloat(form.cost) || 0,
      category: form.category,
      stock: parseInt(form.stock) || 0
    };
    if (editing) {
      await supabase.from('products').update(fields).eq('id', editing);
    } else {
      await supabase.from('products').insert([fields]);
    }
    await fetchProducts();
    setSaving(false);
    closeForm();
  }

  async function toggleAvailable(p) {
    await supabase.from('products')
      .update({ available: !p.available })
      .eq('id', p.id);
    fetchProducts();
  }

  async function deleteProduct(id) {
    if (!window.confirm('Delete this product?')) return;
    await supabase.from('products').delete().eq('id', id);
    fetchProducts();
  }

  async function handleImportFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setImportError('');
    setImporting(true);
    try {
      const payload = await fileToPayload(file);
      const { data, error } = await supabase.functions.invoke('menu-import', {
        body: { ...payload, categories: orgCategories },
      });
      if (error || !data?.items) {
        setImportError(tr.importError);
      } else if (data.items.length === 0) {
        setImportError(tr.importNoItems);
      } else {
        setImportItems(
          data.items.map(i => ({
            name: String(i.name || '').slice(0, 80),
            price: Number(i.price) || 0,
            category: String(i.category || orgCategories[0]).slice(0, 40),
          }))
        );
      }
    } catch (err) {
      setImportError(tr.importError);
    }
    setImporting(false);
  }

  function updateImportItem(idx, field, value) {
    setImportItems(prev =>
      prev.map((item, i) => (i === idx ? { ...item, [field]: value } : item))
    );
  }

  function removeImportItem(idx) {
    setImportItems(prev => prev.filter((_, i) => i !== idx));
  }

  async function confirmImport() {
    if (!importItems || importItems.length === 0) return;
    setSaving(true);

    // Add any brand-new categories proposed by the AI to the shop's list
    // (only the owner is allowed to; for others the update is simply refused
    // by the database and the products still import fine)
    const newCats = [...new Set(importItems.map(i => i.category))]
      .filter(c => c && !orgCategories.includes(c));
    if (newCats.length > 0) {
      await supabase
        .from('organizations')
        .update({ categories: [...orgCategories, ...newCats] })
        .eq('id', org.id);
      await refreshProfile();
    }

    await supabase.from('products').insert(
      importItems
        .filter(i => i.name && i.price > 0)
        .map(i => ({
          name: i.name,
          price: i.price,
          category: i.category,
          stock: 100,
          available: true,
        }))
    );

    await fetchProducts();
    setSaving(false);
    setImportItems(null);
  }

  async function updateStock(p, amount) {
    const newStock = Math.max(0, (p.stock || 0) + amount);
    await supabase.from('products').update({ stock: newStock }).eq('id', p.id);
    fetchProducts();
  }

  function getStockColor(stock) {
    if (stock === 0) return 'zero';
    if (stock <= 3) return 'low';
    if (stock <= 10) return 'medium';
    return 'good';
  }

  const grouped = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = products.filter(p => p.category === cat);
    return acc;
  }, {});

  if (loading) return <Loader text={tr.loadingMenu} />;

  return (
    <div className="menu-editor">
      <div className="me-header">
        <h2>{tr.menuEditor}</h2>
        <div className="me-header-btns">
          <button
            className="import-btn"
            onClick={() => {
              if (MENU_IMPORT_ENABLED) {
                fileInputRef.current?.click();
              } else {
                setImportError('');
                setImportSoon(true);
                setTimeout(() => setImportSoon(false), 5000);
              }
            }}
            disabled={importing}
          >
            {importing ? tr.importAnalyzing : tr.importMenu}
          </button>
          <button className="add-btn" onClick={openAdd}>{tr.addItem}</button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf"
          style={{ display: 'none' }}
          onChange={handleImportFile}
        />
      </div>

      {importError && (
        <div className="import-error">{importError}</div>
      )}

      {importSoon && (
        <div className="import-soon">{tr.importSoon}</div>
      )}

      {products.filter(p => p.stock <= 3 && p.available).length > 0 && (
        <div className="low-stock-banner">
          {tr.lowStock}: {products
            .filter(p => p.stock <= 3 && p.available)
            .map(p => p.name)
            .join(', ')}
        </div>
      )}

      {CATEGORIES.map(cat => (
        <div key={cat} className="category-section">
          <h3 className="cat-title">{getCatLabel(cat)}</h3>
          {grouped[cat].length === 0 && (
            <p className="empty-cat">{tr.noItemsYet}</p>
          )}
          {grouped[cat].map(p => (
            <div key={p.id} className={`me-card ${!p.available ? 'unavailable' : ''}`}>
              <div className="me-info">
                <span className="me-name">{p.name}</span>
                <span className="me-price">{p.price} {tr.currency}</span>
                <div className="stock-row">
                  <button className="stock-btn" onClick={() => updateStock(p, -1)}>−</button>
                  <span className={`stock-count ${getStockColor(p.stock || 0)}`}>
                    📦 {p.stock || 0} {tr.inStock}
                  </span>
                  <button className="stock-btn" onClick={() => updateStock(p, 1)}>+</button>
                </div>
              </div>
              <div className="me-actions">
                <button
                  className={`toggle-btn ${p.available ? 'on' : 'off'}`}
                  onClick={() => toggleAvailable(p)}
                >{p.available ? 'ON' : 'OFF'}</button>
                <button className="edit-btn" onClick={() => openEdit(p)}>✏️</button>
                <button className="delete-btn" onClick={() => deleteProduct(p.id)}>🗑️</button>
              </div>
            </div>
          ))}
        </div>
      ))}

      {importItems && (
        <div className="modal-overlay" onClick={() => setImportItems(null)}>
          <div className="modal import-modal" onClick={e => e.stopPropagation()}>
            <h3>{tr.importDetected} ({importItems.length})</h3>

            <div className="import-rows">
              {importItems.map((item, idx) => {
                const catOptions = [...new Set([
                  ...CATEGORIES,
                  ...importItems.map(i => i.category),
                ])].filter(Boolean);
                return (
                  <div key={idx} className="import-row">
                    <input
                      type="text"
                      className="import-name"
                      value={item.name}
                      onChange={e => updateImportItem(idx, 'name', e.target.value)}
                    />
                    <input
                      type="number"
                      className="import-price"
                      value={item.price}
                      onChange={e => updateImportItem(idx, 'price', parseFloat(e.target.value) || 0)}
                    />
                    <select
                      className="import-cat"
                      value={item.category}
                      onChange={e => updateImportItem(idx, 'category', e.target.value)}
                    >
                      {catOptions.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button className="import-remove" onClick={() => removeImportItem(idx)}>✕</button>
                  </div>
                );
              })}
            </div>

            <div className="modal-btns">
              <button className="cancel-btn" onClick={() => setImportItems(null)}>{tr.cancel}</button>
              <button className="save-btn" onClick={confirmImport} disabled={saving}>
                {saving ? tr.saving : tr.importAddAll.replace('{n}', importItems.length)}
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>{editing ? tr.editItem : tr.addNewItem}</h3>

            <label>{tr.name}</label>
            <input
              type="text"
              placeholder="e.g. Matcha Latte"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
            />

            <label>{tr.price}</label>
            <input
              type="number"
              placeholder="e.g. 22"
              value={form.price}
              onChange={e => setForm({ ...form, price: e.target.value })}
            />

            {isPro && (
              <>
                <label>{tr.costLabel}</label>
                <input
                  type="number"
                  placeholder="e.g. 5"
                  value={form.cost}
                  onChange={e => setForm({ ...form, cost: e.target.value })}
                />
                {form.price && form.cost && parseFloat(form.price) > 0 && (
                  <span className="margin-hint">
                    {tr.marginLabel}: {(parseFloat(form.price) - parseFloat(form.cost)).toFixed(2)} {tr.currency}
                    {' '}({Math.round((1 - parseFloat(form.cost) / parseFloat(form.price)) * 100)}%)
                  </span>
                )}
              </>
            )}

            <label>{tr.stockQty}</label>
            <input
              type="number"
              placeholder="e.g. 50"
              value={form.stock}
              onChange={e => setForm({ ...form, stock: e.target.value })}
            />

            <label>{tr.category}</label>
            <select
              value={form.category}
              onChange={e => setForm({ ...form, category: e.target.value })}
            >
              {CATEGORIES.map(c => (
                <option key={c} value={c}>{getCatLabel(c)}</option>
              ))}
            </select>

            <div className="modal-btns">
              <button className="cancel-btn" onClick={closeForm}>{tr.cancel}</button>
              <button className="save-btn" onClick={saveProduct} disabled={saving}>
                {saving ? tr.saving : tr.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MenuEditor;